import { getLabelFromStorage, consoleEvents } from '@pb/utils'
import { batch } from 'redux-act'

import appSelectors from 'App/duck/selectors'
import {
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_TRACKING_SECTIONS,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
} from 'features/checkout/constants'
import {
  fetchCnCPointsFail,
  fetchCnCPointsStart,
  fetchCnCPointsSuccess,
  fetchPickupPointsFail,
  fetchPickupPointsStart,
  fetchPickupPointsSuccess,
} from 'features/checkout/duck/actions'
import checkoutSelectors from 'features/checkout/duck/selectors'
import {
  fetchPickupPlacesByCoordinates,
  fetchPickupPlacesByPostalCode,
  fetchPickupPlacesByAddress,
} from 'features/checkout/requests'
import { Coordinates, PickupPlace } from 'features/checkout/types'
import { showNotification } from 'features/notifications/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'

import { trackError } from '../track'

type OperationGetPickupPlaces = (
  { postalCode, coordinates }: { postalCode: string; coordinates?: Coordinates },
  deliveryModeCode: string,
) => ThunkResult<Promise<void>>

export const operationGetPickupPlaces: OperationGetPickupPlaces =
  ({ postalCode, coordinates }, deliveryModeCode) =>
  async (dispatch, getState) => {
    const state = getState()
    const deliveryAddress = checkoutSelectors.getDeliveryAddress(state)
    const fetchByAddress =
      appSelectors.getShouldPassCityWithPSS(state) &&
      appSelectors.getShouldPassProvinceWithPSS(state)
    dispatch(fetchPickupPointsStart())
    try {
      let pickupPoint: PickupPlace[] = []
      if (coordinates) {
        pickupPoint = await fetchPickupPlacesByCoordinates(coordinates, [deliveryModeCode])
      } else if (postalCode && fetchByAddress) {
        pickupPoint = await fetchPickupPlacesByAddress(
          postalCode,
          deliveryAddress?.town || '',
          deliveryAddress?.province || '',
          [deliveryModeCode],
        )
      } else if (postalCode) {
        pickupPoint = await fetchPickupPlacesByPostalCode(postalCode, [deliveryModeCode])
      }
      dispatch(fetchPickupPointsSuccess(pickupPoint))
    } catch (error) {
      consoleEvents.error('Failed to fetch pickup points', error)
      const { code } = getErrorData(error)
      const errorMessage = getLabelFromStorage(TECHNICAL_ERROR_LABELS.gettingPUP)

      dispatch(
        batch(
          showNotification({
            content: errorMessage,
            type: 'error',
            timespan: SHOW_NOTIFICATION_TIME,
          }),
          fetchPickupPointsFail(),
        ),
      )

      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.deliveryLocation,
          message: errorMessage,
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    }
  }

export const operationGetCnCPoints: OperationGetPickupPlaces =
  ({ postalCode, coordinates }, deliveryModeCode) =>
  async (dispatch) => {
    dispatch(fetchCnCPointsStart())
    try {
      let cncPoints: PickupPlace[] = []
      if (coordinates) {
        cncPoints = await fetchPickupPlacesByCoordinates(coordinates, [deliveryModeCode])
      } else if (postalCode) {
        cncPoints = await fetchPickupPlacesByPostalCode(postalCode, [deliveryModeCode])
      }
      dispatch(fetchCnCPointsSuccess(cncPoints))
    } catch (error) {
      consoleEvents.error('Failed to fetch cnc points', error)
      const { code } = getErrorData(error)
      const errorMessage = getLabelFromStorage(TECHNICAL_ERROR_LABELS.generalDelivery)

      dispatch(
        batch(
          showNotification({
            content: errorMessage,
            type: 'error',
            timespan: SHOW_NOTIFICATION_TIME,
          }),
          fetchCnCPointsFail(),
        ),
      )

      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.deliveryLocation,
          message: errorMessage,
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    }
  }
